import type { ReactNode } from "react";
import { Outlet } from "react-router-dom";
import styled from "styled-components";
import { MainFooter } from "./MainFooter";
import { Navigation } from "./Navigation";
import {
	MOBILE_DOCKED_BAR_HEIGHT,
	MOBILE_GUTTER,
	MOBILE_NAV_HEIGHT,
	MOBILE_PAGE_PADDING_TOP,
	SAFE_AREA,
	mediaPhone,
} from "@/shared/theme/breakpoints";
import { hideWhileTyping } from "@/shared/theme/keyboard";

const LayoutContainer = styled.div`
	height: 100vh;
	height: 100dvh;
	display: flex;
	flex-direction: row;
	background-color: #ebeefd;
	overflow: hidden;

	/*
	 * Phones: a page with a back button is a screen inside a section, so the bottom bar steps aside
	 * and the page gives its space back. The back button is the way out.
	 */
	${mediaPhone`
		&:has([data-back-button]) {
			> [data-app-nav] {
				display: none;
			}

			> main {
				/* The footer's own padding is the only space below: its link sits near the screen bottom. */
				padding-bottom: ${SAFE_AREA.bottom};
				scroll-padding-bottom: calc(${SAFE_AREA.bottom} + 16px);
			}
		}

		/* A bar docked to the screen bottom (data-docked-bar) must not cover the end of the page. */
		&:has([data-back-button]):has([data-docked-bar]) > main {
			padding-bottom: calc(${MOBILE_DOCKED_BAR_HEIGHT}px + ${SAFE_AREA.bottom});
			scroll-padding-bottom: calc(${MOBILE_DOCKED_BAR_HEIGHT}px + ${SAFE_AREA.bottom} + 16px);
		}
	`}
`;

const SidebarContainer = styled.div`
	width: 80px;
	min-height: 100vh;
	position: fixed;
	left: ${SAFE_AREA.left};
	top: 0;
	z-index: 1000;

	${mediaPhone`
		width: 100%;
		min-height: 0;
		top: auto;
		right: 0;
		bottom: 0;
		left: 0;
	`}

	/* The bar steps aside while the on-screen keyboard is up, so the keyboard doesn't lift it. */
	${hideWhileTyping()}
`;

const ContentContainer = styled.main`
	flex: 1;
	margin-left: calc(80px + ${SAFE_AREA.left});
	overflow-y: auto;
	scrollbar-gutter: stable;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	background-color: #ebeefd;

	/* Phone: no rail offset; keep content clear of the bottom bar. */
	${mediaPhone`
		margin-left: 0;
		padding: ${MOBILE_PAGE_PADDING_TOP}px calc(${MOBILE_GUTTER}px + ${SAFE_AREA.right}) calc(${MOBILE_NAV_HEIGHT}px + ${SAFE_AREA.bottom})
			calc(${MOBILE_GUTTER}px + ${SAFE_AREA.left});
		min-height: 0;
		scrollbar-gutter: auto;
		/* Elements scrolled into view (focus, anchors) stop above the bottom bar, not under it. */
		scroll-padding-bottom: calc(${MOBILE_NAV_HEIGHT}px + ${SAFE_AREA.bottom} + 16px);
	`}
`;

const ContentBody = styled.div`
	flex: 1 0 auto;
	margin-bottom: 64px;

	/* Pin route loaders to the viewport so the icon lines up exactly with the
	   full-page auth loader, independent of the sidebar and scrollbar gutter.
	   The sidebar (z-index 1000) still paints on top. */
	[data-full-page-loader] {
		position: fixed;
		inset: 0;
	}
`;

export const RootLayout = ({ sidebarExtra }: { sidebarExtra?: ReactNode } = {}) => {
	return (
		<LayoutContainer>
			<SidebarContainer data-app-nav>
				<Navigation menuExtra={sidebarExtra} />
			</SidebarContainer>
			<ContentContainer>
				<ContentBody>
					<Outlet />
				</ContentBody>
				<MainFooter />
			</ContentContainer>
		</LayoutContainer>
	);
};
